import * as React from 'react';
import { useTheme, alpha } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { useReactiveVar } from '@apollo/client';
import { useFormik } from 'formik';
import _ from 'lodash';
import * as yup from 'yup';
import * as Device from 'react-device-detect';

import { conversationIdVar } from '@context';
import { useSession, useToastError } from '@hooks';
import { handleLineBreak } from '@utils';
import {
  useSendMessageMutation,
  useGetConversationInfosQuery,
  useOnTypingSubscription,
  useSetTypingMutation,
} from '@graphql';
import { Typing } from './Typing';

const MIN_ROWS = 1;
const MAX_ROWS = 6;

interface AddMessageProps {
  // setLines: React.Dispatch<React.SetStateAction<number>>;
  setComposerHeight: (height: number) => void;
}

export function AddMessage(props: Readonly<AddMessageProps>) {
  const {
    // setLines,
    setComposerHeight,
  } = props;
  const [typings, setTypings] = React.useState<string[]>([]);
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const isTypingRef = React.useRef(false);
  const stopTypingTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const { user, account } = useSession();
  const theme = useTheme();
  const conversationId = useReactiveVar(conversationIdVar);
  const toastError = useToastError();
  const { t } = useTranslation('conversation');

  const { data } = useGetConversationInfosQuery({
    skip: !account || conversationId === null,
    variables: {
      conversationId: conversationId ?? '',
    },
  });

  const [sendMessage] = useSendMessageMutation();
  const [setTypingMutation] = useSetTypingMutation();

  const formik = useFormik({
    initialValues: {
      content: '',
    },
    validationSchema: yup.object({
      content: yup.string().min(1).required(),
    }),
    onSubmit: (values, { resetForm }) => {
      sendMessage({
        variables: {
          conversationId: conversationId || '',
          content: values.content,
        },
        onCompleted() {
          resetForm();
          // Stop typing explicitly on send
          if (isTypingRef.current) {
            isTypingRef.current = false;
            notifyTyping(false);
          }
        },
        onError: toastError,
      });
    },
  });

  useOnTypingSubscription({
    variables: { conversationId: conversationId! },
    skip: !conversationId || !account,
    onData: ({ data }) => {
      const event = data.data?.typing;
      if (!event) return;

      // Ignore my own typing events
      if (event.senderId === account?.id) return;

      const areTyping = new Set(typings);

      if (event.isTyping) {
        areTyping.add(event.senderId);
      } else {
        areTyping.delete(event.senderId);
      }

      setTypings([...areTyping]);
    },
  });

  const notifyTyping = React.useCallback(
    (isTyping: boolean) => {
      if (!conversationId) return;

      setTypingMutation({
        variables: {
          conversationId,
          isTyping,
        },
      }).catch(() => {
        // error already handled by onError
      });
    },
    [conversationId, setTypingMutation]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    formik.handleChange(e);

    if (!conversationId) return;

    const value = e.target.value;

    if (value.trim().length > 0) {
      // L'utilisateur est en train d'écrire
      if (!isTypingRef.current) {
        isTypingRef.current = true;
      }

      // On envoie isTyping: true tant qu'il tape
      notifyTyping(true);
    } else {
      // Le champ est vide → plus en train d'écrire
      if (isTypingRef.current) {
        isTypingRef.current = false;
        notifyTyping(false);
      }
    }

    // Reset stop-typing timer : après 2s sans frappe, on envoie isTyping: false
    if (stopTypingTimeoutRef.current) {
      clearTimeout(stopTypingTimeoutRef.current);
    }

    stopTypingTimeoutRef.current = setTimeout(() => {
      if (isTypingRef.current) {
        isTypingRef.current = false;
        notifyTyping(false);
      }
    }, 2000);
  };

  const handleSend = (e: React.KeyboardEvent<HTMLDivElement>) => {
    return handleLineBreak(e, formik.values.content, formik.submitForm);
  };

  React.useLayoutEffect(() => {
    if (!rootRef.current) return;

    const el = rootRef.current;

    const update = () => {
      setComposerHeight(el.offsetHeight);
    };

    update();

    const ro = new ResizeObserver(() => update());
    ro.observe(el);

    return () => ro.disconnect();
  }, [props]);

  // React.useEffect(() => {
  //   const el = inputRef.current;
  //   if (!el) return;

  //   const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
  //   const currentLines = Math.round(el.scrollHeight / lineHeight);
  //   const linesToDisplay = Math.min(MAX_ROWS, Math.max(MIN_ROWS, currentLines));
  //   setLines(linesToDisplay);
  //   setInputHeight(lineHeight);
  // }, [formik.values.content]);

  React.useEffect(() => {
    // only focus if the dialog is open AND we have a conversation loaded
    if (Device.isDesktop && conversationId !== null && data?.conversation) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [conversationId, data?.conversation]);

  if (!user || conversationId === null || !data?.conversation) {
    return null;
  }

  const borderRadius = '30px';

  return (
    <Stack
      ref={rootRef}
      position="absolute"
      component="form"
      direction="row"
      left={10}
      bottom={10}
      right={10}
      onSubmit={formik.handleSubmit}
    >
      <TextField
        fullWidth
        multiline
        minRows={1}
        maxRows={4}
        autoComplete="off"
        placeholder={t('Write...')}
        size="small"
        id="content"
        name="content"
        variant="outlined"
        value={formik.values.content}
        onChange={handleChange}
        onKeyDown={handleSend}
        sx={{
          borderRadius,
          borderColor: 'transparent',
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(10px)',
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'transparent',
              borderRadius,
            },
            '&:hover fieldset': {
              borderColor: theme.palette.grey[800],
              borderRadius,
            },
            '&.Mui-focused fieldset': {
              borderRadius,
              borderColor: theme.palette.grey[800],
              borderWidth: '1px',
            },
          },
          input: {
            color: theme.palette.text.primary,
          },
          textarea: {
            color: theme.palette.text.primary,
            overflowY: 'auto',
            resize: 'none',
          },
        }}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end" sx={{ alignSelf: 'center' }}>
                <IconButton
                  aria-label="send"
                  size="small"
                  type="submit"
                  disabled={!formik.values.content.trim().length}
                  color={!formik.values.content.trim().length ? 'primary' : 'uncolored'}
                  sx={{
                    backgroundColor: 'black',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#333',
                    },
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                  }}
                >
                  <ArrowUpwardIcon fontSize="small" sx={{ p: 0.05 }} />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
    </Stack>
  );
}
